import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  Fragment,
} from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { Card, Box, Typography, CircularProgress } from "@mui/material";
import { CardBody, Col, Row } from "reactstrap";
import { get, isEmpty } from "lodash";

import FileInput from "./FileInput";
import DocumentCategory from "./DocumentCategory";
import { getOptionValue, powerRequirementOptions } from "common/enums";
import { applicationTypes } from "common/constant";

import { useLazyDocumentTypesByApplicationIdQuery } from "apis/DocumentType";

import getFileExtension from "utils/getFileExtension";
import { getImageUrl } from "utils";
import { getIsDocumentRequired } from "utils/getIsDocumentRequired";

import pdfImg from "assets/images/pdf.png";
import docxImg from "assets/images/docx.png";

const fileTypes = ["pdf", "docx", "doc"];

const PROOF_OF_OWNERSHIP_DESCRIPTION =
  "Any of the following documents shall be considered as acceptable proof of ownership or occupancy of premises for which connection is required:";

const isValidFileType = (fileName) => {
  const fileExtension = getFileExtension(fileName).toLowerCase();
  return fileTypes.includes(fileExtension);
};

const DocumentToSubmit = ({ formik, t }) => {
  // documentCategoryData
  const { values, setFieldValue, errors, touched } = formik;

  const {
    utilityId,
    utilities,
    applicant,
    utilityAddress,
    applicationTypeId,
    connectionLoad,
    parentId,
    documents,
    id,
    utilityConsumerId,
    isIGSTExempted,
  } = values;

  const { applicantTypeId, occupancyTypeId } = applicant ?? {};
  const { premisesAreaId, isBPL } = utilityAddress ?? {};
  const { tention, increaseOfLoad, decreaseOfLoad, isGreenPowerRequired } =
    connectionLoad ?? {};
  const [documentQuery, setDocumentQuery] = useState({});

  const [getDocumentTypes, { data: { data: documentList } = {}, isLoading }] =
    useLazyDocumentTypesByApplicationIdQuery();

  const isNameTransferApplication = useMemo(
    () => applicationTypes["Name Change/Transfer"] === applicationTypeId,
    [applicationTypeId]
  );

  useEffect(() => {
    setDocumentQuery({
      applicantTypeId: applicantTypeId ?? "",
      occupancyTypeId: occupancyTypeId ?? "",
      utilities: utilityId ? [utilityId] : utilities.map(({ value }) => value),
      isBPL: isBPL ?? false,
      isGreenPower: isGreenPowerRequired ?? false,
      // isRural:
      //    !!utilityAddress?.isRural ?? false,
      loadType:
        utilityId ||
        utilities?.some(
          ({ value }) => value === getOptionValue("utilityOptions", "Power")
        )
          ? tention === powerRequirementOptions[1].label
            ? powerRequirementOptions[1].value
            : powerRequirementOptions[0].value
          : "",
      isDemandAlteration: increaseOfLoad > 0 || decreaseOfLoad > 0,
      applicationId: id || "",
      parentId: parentId ?? "",
      premiseAreaId: premisesAreaId ?? "",
      utilityConsumerId: utilityConsumerId ?? "",
      isIGSTExempted: isIGSTExempted ?? false,
    });
  }, [
    applicantTypeId,
    occupancyTypeId,
    isBPL,
    utilityId,
    // utilityAddress?.isRural,
    tention,
    parentId,
    premisesAreaId,
    id,
  ]);

  const getDocuments = useCallback(
    async (applicationTypeId, query = {}) => {
      try {
        const { data: documentList } = await getDocumentTypes(
          applicationTypeId,
          query
        );
        return documentList || [];
      } catch (error) {}
      return [];
    },
    [getDocumentTypes, documentQuery]
  );

  useEffect(() => {
    if (!isEmpty(documentQuery)) {
      (async () => {
        const documentList = await getDocuments(
          applicationTypeId,
          documentQuery
        );

        if (documentList.length) {
          const documentTypeIds = [
            0,
            ...documentList.flatMap(({ documentTypes }) =>
              documentTypes.map(({ documentTypeId }) => documentTypeId)
            ),
          ];

          setFieldValue(
            "documents",
            documents.filter(({ documentTypeId }) =>
              documentTypeIds.includes(documentTypeId)
            )
          );

          const requiredDocumentsCount = documentList?.reduce(
            (res, { documentCategoryDescription, documentTypes }) => {
              if (documentCategoryDescription) {
                const isRequired = getIsDocumentRequired(
                  documentTypes[0].documentRequiredEnum
                );

                if (isRequired) {
                  return res + 1;
                }
                return res;
              } else {
                let count = 0;
                documentTypes.forEach(({ documentRequiredEnum }) => {
                  const isRequiredType =
                    getIsDocumentRequired(documentRequiredEnum);

                  if (isRequiredType) {
                    count++;
                  }
                });

                return res + count;
              }
            },
            0
          );

          setFieldValue(
            "extraValues.requiredDocumentsCount",
            requiredDocumentsCount
          );

          const proofOfOwnershipDescriptions = [
            ...documentList.reduce(
              (acc, { documentCategoryDescription, documentTypes }) => {
                if (
                  documentCategoryDescription === PROOF_OF_OWNERSHIP_DESCRIPTION
                ) {
                  return documentTypes.map(({ documentTypeDescription }) =>
                    documentTypeDescription.trim().toLowerCase()
                  );
                }
                return acc;
              },
              []
            ),
            "other",
          ];
          setFieldValue(
            "extraValues.proofOfOwnershipTypes",
            proofOfOwnershipDescriptions
          );
        }
      })();
    }
  }, [documentQuery, applicationTypeId]);

  const isError = !!(
    get(touched, "documents", "") && get(errors, "documents", "")
  );

  // For removing documents which does not match to its parent id in name transfer form

  useEffect(() => {
    if (isNameTransferApplication && documentList?.length) {
      const documentTypeIds = [
        0,
        ...documentList.flatMap(({ documentTypes }) =>
          documentTypes.map(({ documentTypeId }) => documentTypeId)
        ),
      ];

      const updatedDocuments = documents.filter((document) =>
        documentTypeIds.includes(document.documentTypeId) && document.parentId
          ? document.parentId === parentId
          : true
      );
      setFieldValue("documents", updatedDocuments);
    }
  }, [parentId, documentList]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center">
        <CircularProgress size={30} />
      </div>
    );
  }

  if (!documentList?.length) {
    return (
      <Card variant="outlined">
        <CardBody className="text-center">
          <Typography variant="body1">{t("No Documents to upload")}</Typography>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="ms-sm-5">
      {documentList.map((document, index) =>
        document.documentCategoryDescription ? (
          <DocumentCategory
            category={document}
            key={index}
            formik={formik}
            isError={isError}
          />
        ) : (
          <Fragment key={index}>
            {document.documentTypes.map(
              (
                {
                  documentTypeId,
                  documentTypeDescription,
                  parentId,
                  documentRequiredEnum,
                  utilities: documentUtilities,
                  imageFileSize,
                  documentFileSize,
                },
                index
              ) => {
                const formikDocument = documents.find(
                  (formikDocument) =>
                    formikDocument?.documentTypeId === documentTypeId
                );

                return (
                  <Row className="mb-2" key={index}>
                    <Col sm={9} className="mb-2">
                      <p className="m-0">
                        {documentTypeDescription}
                        {getIsDocumentRequired(documentRequiredEnum) && (
                          <span className="text-danger">*</span>
                        )}
                      </p>
                    </Col>
                    <Col sm={3} className="mb-2">
                      {formikDocument ? (
                        <>
                          {isValidFileType(
                            formikDocument.file?.name ??
                              formikDocument.documentURL
                          ) ? (
                            <div className="image-container">
                              <a
                                href={
                                  formikDocument.url ||
                                  getImageUrl(formikDocument.documentURL)
                                }
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Box
                                  component="img"
                                  className="image"
                                  alt={
                                    formikDocument.file?.name ||
                                    "document image"
                                  }
                                  src={
                                    getFileExtension(
                                      formikDocument.file?.name ??
                                        formikDocument.documentURL
                                    ).toLowerCase() === "pdf"
                                      ? pdfImg
                                      : docxImg
                                  }
                                  sx={{ width: "80px" }}
                                />
                              </a>
                              <i
                                className="ri-close-circle-line fs-3 close-btn"
                                onClick={() =>
                                  setFieldValue(
                                    "documents",
                                    documents.filter(
                                      (document) =>
                                        document?.documentTypeId !==
                                        formikDocument.documentTypeId
                                    )
                                  )
                                }
                              />
                            </div>
                          ) : (
                            <div className="image-container">
                              <Box
                                component="img"
                                className="image"
                                alt={
                                  formikDocument.file?.name || "document image"
                                }
                                src={
                                  formikDocument.url ||
                                  getImageUrl(formikDocument.documentURL)
                                }
                              />
                              <i
                                className="ri-close-circle-line fs-3 close-btn"
                                onClick={() =>
                                  setFieldValue(
                                    "documents",
                                    documents.filter(
                                      (document) =>
                                        document?.documentTypeId !==
                                        formikDocument.documentTypeId
                                    )
                                  )
                                }
                              />
                            </div>
                          )}
                        </>
                      ) : (
                        <FileInput
                          setFieldValue={setFieldValue}
                          documents={documents}
                          documentTypeId={documentTypeId}
                          utilities={documentUtilities}
                          documentCategoryId={document.documentCategoryId}
                          parentId={parentId}
                          isError={isError}
                          documentRequiredEnum={documentRequiredEnum}
                          imageFileSize={imageFileSize}
                          documentFileSize={documentFileSize}
                        />
                      )}
                    </Col>
                  </Row>
                );
              }
            )}
          </Fragment>
        )
      )}
    </div>
  );
};

DocumentToSubmit.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
};
export default withTranslation()(DocumentToSubmit);
